/**
 * UploadGuard — reusable target-company resolver for ALL multi-tenant uploads.
 *
 * Why this exists:
 *  Backend `require_target_company_id` rejects any upload that lacks a tenant.
 *  This hook is the front-end counterpart — it short-circuits when the session
 *  already has a tenant (95% of users) and only prompts when a Super Admin
 *  uploads without impersonation.
 *
 * Usage:
 *   const { ensureCompanyId, guardModal } = useUploadGuard(user);
 *
 *   const handleUpload = async () => {
 *     const targetCo = await ensureCompanyId();      // null = user cancelled
 *     if (!targetCo) return;
 *     const fd = new FormData();
 *     fd.append('file', file);
 *     fd.append('target_company_id', targetCo);      // satisfies backend dep
 *     await axios.post('/api/.../upload', fd, ...);
 *   };
 *
 *   return (<>{guardModal}{...rest of UI}</>);
 */
import React from 'react';
import axios from 'axios';
import { Building2, X, AlertCircle, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeader = () => {
  const token = localStorage.getItem('access_token') || localStorage.getItem('token');
  return { headers: { Authorization: `Bearer ${token}` } };
};

export function useUploadGuard(user) {
  // Resolved choice for super-admin flow (sticky across uploads in same session)
  const [chosen, setChosen] = React.useState(null);
  // Visible state for the picker modal
  const [open, setOpen] = React.useState(false);
  const [companies, setCompanies] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  // Promise resolver — call .resolve(id) when the user picks; .resolve(null) on cancel
  const resolverRef = React.useRef(null);

  const sessionCo = user?.company_id || null;
  const isSuperAdmin = !!user?.is_super_admin;

  // Fetch companies once when the modal first opens
  const fetchCompanies = React.useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.get(`${API}/companies`, getAuthHeader());
      // Filter active only, sort alphabetically
      const list = (data || [])
        .filter((c) => c.is_active !== false)
        .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setCompanies(list);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not load companies');
    } finally {
      setLoading(false);
    }
  }, []);

  /** Promise-based gate: returns the company_id, or null if the user cancels. */
  const ensureCompanyId = React.useCallback(() => {
    // Path 1: regular user has session tenant — passthrough
    if (sessionCo) return Promise.resolve(sessionCo);
    // Path 2: super admin already picked once this session — reuse
    if (isSuperAdmin && chosen) return Promise.resolve(chosen);
    // Path 3: open modal and wait for the user
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setOpen(true);
      if (companies.length === 0) fetchCompanies();
    });
  }, [sessionCo, isSuperAdmin, chosen, companies.length, fetchCompanies]);

  const handlePick = (companyId) => {
    setChosen(companyId);
    setOpen(false);
    resolverRef.current?.(companyId);
    resolverRef.current = null;
  };

  const handleCancel = () => {
    setOpen(false);
    resolverRef.current?.(null);
    resolverRef.current = null;
  };

  /** Allow consumers to reset the sticky choice (e.g. on logout). */
  const resetChosen = React.useCallback(() => setChosen(null), []);

  const guardModal = open ? (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
        data-testid="upload-guard-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Select target company"
      >
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div
              className="w-9 h-9 rounded-lg flex items-center justify-center text-white"
              style={{ background: 'linear-gradient(135deg, #4F46E5 0%, #DB2777 100%)' }}
            >
              <Building2 size={16} />
            </div>
            <div>
              <h3 className="text-[15px] font-semibold text-slate-900">Select Target Company</h3>
              <p className="text-[11.5px] text-slate-500">
                Super Admin upload — tag the imported rows to a specific tenant.
              </p>
            </div>
          </div>
          <button
            onClick={handleCancel}
            data-testid="upload-guard-close"
            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100"
            aria-label="Cancel"
          >
            <X size={16} />
          </button>
        </div>

        <div className="px-5 py-4 max-h-[60vh] overflow-y-auto">
          {loading && (
            <div className="flex items-center justify-center py-8 text-slate-500 text-[13px]">
              <Loader2 size={16} className="animate-spin mr-2" />
              Loading companies…
            </div>
          )}
          {error && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-rose-50 text-rose-700 text-[12.5px]">
              <AlertCircle size={15} className="shrink-0 mt-0.5" />
              <div>{error}</div>
            </div>
          )}
          {!loading && !error && companies.length === 0 && (
            <p className="text-[12.5px] text-slate-500 text-center py-6">
              No active companies found.
            </p>
          )}
          {!loading && !error && companies.length > 0 && (
            <ul className="space-y-1.5">
              {companies.map((c) => (
                <li key={c.id}>
                  <button
                    onClick={() => handlePick(c.id)}
                    data-testid={`upload-guard-pick-${c.id}`}
                    className="w-full text-left px-3 py-2.5 rounded-xl border border-slate-200 hover:border-indigo-300 hover:bg-indigo-50/50 transition-colors group"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-[13px] font-medium text-slate-800 group-hover:text-indigo-700">
                          {c.name}
                        </div>
                        <div className="text-[11px] text-slate-400 mt-0.5">
                          Code: <span className="font-mono">{c.code}</span>
                          {c.city ? ` · ${c.city}` : ''}
                        </div>
                      </div>
                      <span className="text-[11px] text-slate-400 group-hover:text-indigo-500">→</span>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="px-5 py-3 bg-slate-50 border-t border-slate-100 flex items-center justify-between">
          <p className="text-[11px] text-slate-500">
            Cancel to abort. Choice is remembered for this session.
          </p>
          <button
            onClick={handleCancel}
            data-testid="upload-guard-cancel"
            className="px-3 py-1.5 text-[12px] text-slate-700 hover:bg-white rounded-lg border border-slate-200"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  ) : null;

  return { ensureCompanyId, guardModal, resetChosen, chosenCompanyId: chosen || sessionCo };
}

export default useUploadGuard;
